import type { IGroupTransfer } from "@/modules/transaction/common/types";
import { getBankName } from "@/modules/transaction/helpers";
import { CloudflareAsset, Currencies } from "@/shared/common/enums";
import { Amount, CloudFlareImage } from "@/shared/components";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { type RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: IGroupTransfer;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
  onShowBeneficiaries: () => void;
}

export const GroupTransferDetailRows = ({
  transfer,
  scrollbarContainerRef,
  onShowBeneficiaries,
}: Props) => {
  const { t } = useTranslation();

  const bankName = useMemo(() => {
    return t(getBankName(transfer.beneficiaries?.[0]?.receiverAccount));
  }, [transfer]);

  return (
    <div>
      <div>
        <CollapseGroup
          className="detail-collapse"
          items={[
            {
              content: (
                <>
                  <div>
                    <div
                      className="pt-20 pb-20 detail-row beneficiary-link-row"
                      onClick={onShowBeneficiaries}
                    >
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.group.beneficiaryPayeeTotalCount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Text className="pr-4" size="medium" weight="bold">
                          {transfer.beneficiaries?.length}
                        </Text>
                        <CloudFlareImage path={CloudflareAsset.ArrowShortRight}/>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.beneficiaryBank")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="bankName-tooltip"
                        text={bankName}
                      />
                      <Text
                        id="bankName-tooltip"
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {bankName}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "beneficiary_payee_details",
              isOpen: true,
              title: t("transfer.group.beneficiaryPayeeDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.group.accountFrom")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.debitAccount?.number as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.debitAccount?.number}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.group.totalAmount")}
                      </Text>
                      <Amount
                        currency={transfer.currency}
                        amount={transfer.amount as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.purpose")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="group-transfer-purpose-tooltip"
                        text={transfer.purpose}
                      />
                      <Text
                        id="group-transfer-purpose-tooltip"
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {transfer.purpose}
                      </Text>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.commissionFee")}
                      </Text>
                      <Amount
                        currency={Currencies.AMD}
                        amount={transfer.commissionAmount}
                      />
                    </div>
                    {transfer.commissionAccount?.number && (
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                    )}
                  </div>
                  {transfer.commissionAccount?.number && (
                    <div>
                      <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.feeFrom")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Copy
                            textAfterCopy={t("general.copied")}
                            size="medium"
                            parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                            text={transfer.commissionAccount?.number}
                          />
                          <Text size="medium" weight="bold">
                            {transfer.commissionAccount?.number}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ),
              value: "transfer_details",
              isOpen: true,
              title: t("transfer.transferDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.transactionNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.transactionNumber as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.transactionNumber}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text type="secondary" size="medium" weight="semibold">
                        {t("general.date")}
                      </Text>
                      <Text size="medium" weight="bold">
                        {transfer.scheduleDate}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "other_details",
              isOpen: true,
              title: t("general.otherDetails")
            },
          ]}
        />
      </div>
    </div>
  );
};
